import { useHasScopes } from '@/hooks/useHasScopes';
import { trpc } from '@/trpcClient';
import { cn } from '@/utils/utils';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// 'waiting' is a display-only state: pending, but its campaign isn't playing yet (only the
// campaign the panel is showing right now gets converted)
type VariantStatus = 'ready' | 'processing' | 'pending' | 'failed' | 'missing' | 'waiting';

const STATUS_LABEL: Record<VariantStatus, string> = {
  ready: 'Pronto',
  processing: 'Processando…',
  pending: 'Na fila',
  failed: 'Falhou',
  missing: 'Não gerado',
  waiting: 'Aguarda início da campanha',
};

const STATUS_CLASS: Record<VariantStatus, string> = {
  ready: 'bg-emerald-500/15 text-emerald-400',
  processing: 'bg-sky-500/15 text-sky-400',
  pending: 'bg-amber-500/15 text-amber-400',
  failed: 'bg-red-500/15 text-red-400',
  missing: 'bg-zinc-500/15 text-zinc-400',
  waiting: 'bg-zinc-500/15 text-zinc-400',
};

// Refresh often while something is still on its way, so the status changes on its own
const ACTIVE_POLL_MS = 10_000;

/**
 * "Vídeos para o painel": status of the panel-sized version of each video of the material's
 * campaigns. Reflects the saved panel settings - unsaved edits in the form above don't count.
 */
export function LedPanelVariantsStatus({ materialUuid }: { materialUuid: string }) {
  const queryClient = useQueryClient();
  const canEditMaterial = useHasScopes(['material:edit']);
  const variantsQuery = trpc.material.ledPanelVariants.queryOptions({ uuid: materialUuid });

  const { data, isPending, isError } = useQuery({
    ...variantsQuery,
    refetchInterval: (query) =>
      query.state.data?.videos.some((v) => (v.status === 'pending' && v.playingNow) || v.status === 'processing')
        ? ACTIVE_POLL_MS
        : false,
  });

  const retryMutation = useMutation(trpc.material.ledPanelVariantsRetry.mutationOptions());
  const retry = () =>
    retryMutation.mutate(
      { uuid: materialUuid },
      {
        onSuccess: () => {
          toast.success('Vídeos recolocados na fila do painel LED');
          queryClient.invalidateQueries({ queryKey: variantsQuery.queryKey });
        },
        onError: (error) => toast.error(`Erro ao recolocar vídeos na fila: ${error.message}`),
      }
    );

  if (isPending) {
    return <p className="text-xs text-[var(--gray-primary)]">Carregando vídeos do painel…</p>;
  }
  if (isError || !data) {
    return <p className="text-xs text-red-400">Não foi possível carregar o status dos vídeos do painel.</p>;
  }
  if (!data.geometry) {
    return (
      <p className="text-xs text-[var(--gray-primary)]">
        Salve o material com as medidas do painel para gerar os vídeos no formato dele.
      </p>
    );
  }

  const { geometry } = data;
  const isEsp32 = geometry.fps !== null;
  const format =
    geometry.fps !== null
      ? `${geometry.widthPx}×${geometry.heightPx} · ${geometry.fps} fps`
      : `${geometry.widthPx}×${geometry.heightPx} em ${geometry.offsetXPx},${geometry.offsetYPx} · saída ${geometry.outputResolution?.replace('x', '×')}`;
  const hasRetryable = data.videos.some((v) => v.status === 'failed' || v.status === 'missing');

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">Vídeos para o painel</h3>
        <span className="text-xs text-[var(--gray-primary)]">Formato salvo: {format}</span>
      </div>

      {data.videos.length === 0 ? (
        <p className="text-xs text-[var(--gray-primary)]">As campanhas deste material não têm vídeos.</p>
      ) : (
        <ul className="divide-y divide-[var(--border)] rounded-md border border-[var(--border)]">
          {data.videos.map((video) => {
            const status: VariantStatus =
              video.status === 'pending' && !video.playingNow ? 'waiting' : (video.status as VariantStatus);
            return (
              <li key={video.mediaUuid} className="flex flex-wrap items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{video.mediaName}</p>
                  <p className="truncate text-xs text-[var(--gray-primary)]">
                    {video.campaignNames.join(', ')}
                    {video.playingNow && <span className="ml-1 text-sky-400">· passando agora</span>}
                  </p>
                  {status === 'failed' && video.errorMessage && (
                    <p className="text-xs break-words text-red-400">{video.errorMessage}</p>
                  )}
                </div>
                <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', STATUS_CLASS[status])}>
                  {STATUS_LABEL[status]}
                </span>
                {video.previewUrl && (
                  <Button type="button" variant="secondary" size="sm" asChild>
                    <a href={video.previewUrl} target="_blank" rel="noreferrer" download={isEsp32 || undefined}>
                      {isEsp32 ? 'Baixar .bin' : 'Ver vídeo'}
                    </a>
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canEditMaterial && hasRetryable && (
        <div>
          <Button type="button" variant="secondary" size="sm" onClick={retry} loading={retryMutation.isPending}>
            Tentar de novo
          </Button>
        </div>
      )}
    </div>
  );
}
